import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { useUser } from '../lib/auth';
import { useQueryClient } from '@tanstack/react-query';
import { issuesApi } from '../lib/api';
import type { CreateIssueRequest, UpdateIssueRequest, IssueSeverity, IssueStatus } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { ISSUE_MUTATION_INVALIDATE_KEYS } from './issueMutationInvalidation';

log.info('[useIssues] Module loaded');

export function useIssues(params?: {
  projectId?: string;
  status?: IssueStatus;
  severity?: IssueSeverity;
  type?: string;
}) {
  log.info('[useIssues] useIssues called with params:', { params });
  return useApiQuery({
    queryKey: ['issues', params],
    queryFn: (token) => issuesApi.list(token, params),
    placeholderPreviousData: true,
  });
}

export function useIssue(id: string) {
  log.info('[useIssues] useIssue called with id:', { id });
  return useApiQuery({
    queryKey: ['issues', id],
    queryFn: (token) => issuesApi.get(token, id),
    enabled: !!id,
  });
}

export function useCreateIssue() {
  const { user } = useUser();
  log.info('[useIssues] useCreateIssue called, userId:', { id: user?.id });

  return useApiMutation<unknown, CreateIssueRequest>({
    mutationFn: (token, data) => {
      const userId = user?.id || '';
      return issuesApi.create(token, userId, data);
    },
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
    onSuccess: (_data, variables) => {
      log.info('[useIssues] useCreateIssue onSuccess, projectId:', { projectId: variables.projectId });
    },
  });
}

export function useUpdateIssue() {
  log.info('[useIssues] useUpdateIssue called');
  return useApiMutation<unknown, { id: string; data: UpdateIssueRequest }>({
    mutationFn: (token, { id, data }) => issuesApi.update(token, id, data),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
    onSuccess: (_data, variables) => {
      log.info('[useIssues] useUpdateIssue onSuccess for issue:', { id: variables.id });
    },
  });
}

export function useDeleteIssue() {
  log.info('[useIssues] useDeleteIssue called');
  const queryClient = useQueryClient();
  return useApiMutation<unknown, string>({
    mutationFn: (token, id) => issuesApi.delete(token, id),
    invalidateKeys: ISSUE_MUTATION_INVALIDATE_KEYS,
    onMutate: (id) => {
      // Optimistic: remove the issue from all cached issue lists immediately.
      // The list shape is `{ items: [...] }` (issuesApi.list), never `data` —
      // see useBulkActions.ts's withIssuesRemoved for the sibling bug this
      // guard used to share (a `.data` check against a cache that has no
      // `.data` field, which always short-circuited). This hook itself has no
      // production caller today (no import of useDeleteIssue from this
      // module), so there is nothing to rollback on error yet; fixed for
      // consistency so a future caller doesn't inherit a dead guard.
      queryClient.setQueriesData<{ items?: unknown[] }>(
        { queryKey: ['issues'] },
        (old) => {
          if (!old?.items || !Array.isArray(old.items)) return old;
          return { ...old, items: old.items.filter((i) => (i as { id?: string })?.id !== id) };
        },
      );
    },
  });
}
